// =============================================================================
// toasts.js — les notifications non bloquantes d'Aramis (lot L5, refonte
// « Mosaïque vivante », synthèse §5.2 régime quotidien et §10 L5).
//
// FAÇADE INCHANGÉE (1 264 appels) : `toast.success/error/info/warn(message,
// { duration })`, `useToast()`, `<ToastProvider>` — réexportés tels quels par
// uxComponents.js. Seule l'implémentation change, SANS dépendance (la synthèse
// envisageait Sonner ; le programme L5 exclut toute nouvelle dépendance) :
//   • une PILE façon Sonner : le plus récent au plus près du coin, les autres
//     remontent ; trois visibles au plus, les suivants attendent leur tour ;
//   • des TRANSITIONS CSS, jamais de @keyframes : une entrée, une sortie ou un
//     réagencement interrompu repart de sa position courante ;
//   • une SORTIE animée (200 ms) avant le retrait du DOM ;
//   • la minuterie s'arrête quand l'onglet est caché, au survol ou au focus de
//     la pile, et pendant un glisser ; un toast caché (4e et suivants) ne
//     décompte pas : chaque message est VU pendant toute sa durée ;
//   • glisser pour fermer (au doigt comme à la souris) : au-delà de 45 px ou
//     0,11 px/ms, le toast part dans le sens du geste ;
//   • thème sombre par les jetons --toast-* de theme.css ; au téléphone, pleine
//     largeur AU-DESSUS de la barre d'onglets (--h-onglets) ;
//   • annonce aux lecteurs d'écran : région polie, erreurs en role="alert" ;
//   • un succès CONFIRME un acte : il demande le son de confirmation avec son
//     texte (src/sons.js, muet par défaut et hors geste, en salle, en rafale ;
//     le moteur tait copie, téléchargement, export, aperçu, simulation,
//     « rien à faire » et succès partiel « N en erreur ») ; `{ son: false }`
//     l'en exclut d'office. Jamais de son pour error/warn/info.
// Styles : section « primitives » de src/feuilleGlobale.js (.enio-toasts…).
// =============================================================================
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Icone } from "./icones";
import { jouerSon } from "./sons";

const ToastContext = createContext(null);

// Handler global exposé pour les call-sites hors composants React. Mis à jour
// par le ToastProvider au montage ; repli sur la console tant qu'il n'est pas
// monté (inchangé depuis la version d'origine).
let _globalToastApi = null;

export const DUREES_TOAST = Object.freeze({ success: 3500, info: 3500, warn: 3500, error: 7000 });
export const PILE = Object.freeze({ visibles: 3, ecart: 8, sortieMs: 200, seuilGlisser: 45, vitesseGlisser: 0.11 });

const ICONE_PAR_TYPE = { success: "circle-check", error: "alert-triangle", warn: "alert-triangle", info: "bell" };

// Effet de mise en page, sans avertissement côté serveur.
const useEffetMiseEnPage = typeof window === "undefined" ? useEffect : useLayoutEffect;

/** Décalages verticaux de la pile : le plus récent (index 0) à 0, chacun des
 *  suivants au-dessus des précédents. Les toasts en sortie gardent leur
 *  dernier décalage et ne poussent plus personne. Fonction pure (testée). */
export function calculerPile(toasts, hauteurs, { visibles = PILE.visibles, ecart = PILE.ecart } = {}) {
  const res = {};
  let cumul = 0;
  let rang = 0;
  for (const t of toasts) {
    if (t.sortie) continue;
    res[t.id] = { offset: cumul, rang, visible: rang < visibles };
    cumul += (hauteurs[t.id] || 0) + ecart;
    rang += 1;
  }
  return res;
}

export function ToastProvider({ children }) {
  // Le plus récent EN TÊTE du tableau.
  const [toasts, setToasts] = useState([]);
  const [hauteurs, setHauteurs] = useState({});
  const idRef = useRef(0);
  const minuteries = useRef(new Map()); // id → { restant, debut, poignee }
  // Raisons de pause : « cache », « focus », « survol:<id> », « glisser:<id> ».
  // Survol et glisser sont PORTÉS par un toast : un nœud retiré du DOM ne
  // délivre ni pointerleave ni blur, donc chaque toast lève les siens en se
  // démontant, et le focus est relu sur le DOM après chaque rendu (sinon la
  // pile se figeait après une fermeture par la croix : relecture L5).
  const pauses = useRef(new Set());
  const retraits = useRef(new Map());    // id → poignée du retrait différé
  const derniersOffsets = useRef({});
  const sectionRef = useRef(null);
  const vivant = useRef(true);

  const retirer = useCallback((id) => {
    const m = minuteries.current.get(id);
    if (m && m.poignee) clearTimeout(m.poignee);
    minuteries.current.delete(id);
    if (retraits.current.has(id)) return;
    setToasts((l) => l.map((t) => (t.id === id ? { ...t, sortie: true } : t)));
    retraits.current.set(id, setTimeout(() => {
      retraits.current.delete(id);
      setToasts((l) => l.filter((t) => t.id !== id));
      setHauteurs((h) => { const n = { ...h }; delete n[id]; return n; });
    }, PILE.sortieMs));
  }, []);

  const armer = useCallback((id) => {
    const m = minuteries.current.get(id);
    if (!m || m.poignee || m.restant <= 0 || pauses.current.size) return;
    m.debut = Date.now();
    m.poignee = setTimeout(() => retirer(id), m.restant);
  }, [retirer]);

  const suspendre = useCallback((id) => {
    const m = minuteries.current.get(id);
    if (!m || !m.poignee) return;
    clearTimeout(m.poignee);
    m.poignee = null;
    m.restant = Math.max(0, m.restant - (Date.now() - m.debut));
  }, []);

  const push = useCallback((type, message, opts = {}) => {
    const id = ++idRef.current;
    const duree = opts.duration ?? DUREES_TOAST[type] ?? 3500;
    if (duree > 0) minuteries.current.set(id, { restant: duree, debut: 0, poignee: null });
    setToasts((l) => [{ id, type, message, ts: Date.now(), sortie: false }, ...l]);
    if (type === "success" && opts.son !== false) jouerSon("confirmation", { texte: message });
    return id;
  }, []);

  const dismiss = useCallback((id) => { retirer(id); }, [retirer]);

  const api = useMemo(() => ({
    success: (msg, opts) => push("success", msg, opts),
    error:   (msg, opts) => push("error", msg, opts),
    info:    (msg, opts) => push("info", msg, opts),
    warn:    (msg, opts) => push("warn", msg, opts),
    dismiss,
  }), [push, dismiss]);

  // Expose globalement pour les call-sites non-hook (async handlers, utilitaires)
  useEffect(() => {
    _globalToastApi = api;
    return () => { if (_globalToastApi === api) _globalToastApi = null; };
  }, [api]);

  const pile = calculerPile(toasts, hauteurs);

  // Seuls les toasts VISIBLES décomptent (un message caché attend son tour).
  useEffect(() => {
    for (const t of toasts) {
      const p = pile[t.id];
      if (!p) continue;
      if (p.visible) armer(t.id); else suspendre(t.id);
    }
  });

  // Pause générale : onglet caché, survol, focus, glisser.
  const majPause = useCallback((raison, actif) => {
    if (pauses.current.has(raison) === Boolean(actif)) return;
    if (actif) pauses.current.add(raison); else pauses.current.delete(raison);
    if (pauses.current.size) minuteries.current.forEach((_, id) => suspendre(id));
    else if (vivant.current) setToasts((l) => l.slice()); // relance par l'effet ci-dessus (visibles seulement)
  }, [suspendre]);

  // Le focus ne tient que s'il est RÉELLEMENT dans la pile : la croix focalisée
  // d'un toast retiré (ou la pile démontée) ne délivre aucun blur.
  useEffect(() => {
    if (!pauses.current.has("focus") || typeof document === "undefined") return;
    const s = sectionRef.current;
    if (!s || !s.contains(document.activeElement)) majPause("focus", false);
  });

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const maj = () => majPause("cache", document.visibilityState === "hidden");
    maj();
    document.addEventListener("visibilitychange", maj);
    return () => document.removeEventListener("visibilitychange", maj);
  }, [majPause]);

  useEffect(() => {
    vivant.current = true;
    const mins = minuteries.current; // Map stables, jamais réassignées
    const rets = retraits.current;
    return () => {
      vivant.current = false;
      mins.forEach((m) => { if (m.poignee) clearTimeout(m.poignee); });
      rets.forEach((p) => clearTimeout(p));
    };
  }, []);

  const noterHauteur = useCallback((id, h) => {
    setHauteurs((prev) => (prev[id] === h ? prev : { ...prev, [id]: h }));
  }, []);

  // Un toast en sortie garde son dernier décalage (il s'efface sur place).
  for (const t of toasts) if (pile[t.id]) derniersOffsets.current[t.id] = pile[t.id].offset;

  return (
    <ToastContext.Provider value={api}>
      {children}
      {toasts.length > 0 && (
        <section ref={sectionRef} className="enio-toasts" aria-label="Notifications" aria-live="polite" aria-relevant="additions text"
          onFocus={() => majPause("focus", true)}
          onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) majPause("focus", false); }}>
          {toasts.map((t) => (
            <ToastPile key={t.id} toast={t}
              offset={pile[t.id] ? pile[t.id].offset : (derniersOffsets.current[t.id] || 0)}
              visible={pile[t.id] ? pile[t.id].visible : true}
              onHauteur={noterHauteur}
              onFermer={() => retirer(t.id)}
              onPause={majPause} />
          ))}
        </section>
      )}
    </ToastContext.Provider>
  );
}

function ToastPile({ toast: t, offset, visible, onHauteur, onFermer, onPause }) {
  const ref = useRef(null);
  const [monte, setMonte] = useState(false);
  const geste = useRef(null);
  const survol = `survol:${t.id}`;
  const glisser = `glisser:${t.id}`;

  // Retiré du DOM, un toast ne reçoit plus ni pointerleave ni pointerup :
  // il lève lui-même les pauses qu'il portait.
  useEffect(() => () => { onPause(survol, false); onPause(glisser, false); }, [onPause, survol, glisser]);

  useEffetMiseEnPage(() => {
    const el = ref.current;
    if (!el) return undefined;
    const mesurer = () => onHauteur(t.id, Math.round(el.getBoundingClientRect().height));
    mesurer();
    if (typeof ResizeObserver !== "function") return undefined;
    const ro = new ResizeObserver(mesurer);
    ro.observe(el);
    return () => ro.disconnect();
  }, [t.id, onHauteur]);

  // Entrée : l'état « non monté » est peint une fois, puis la transition part.
  useEffect(() => {
    if (typeof requestAnimationFrame !== "function") { setMonte(true); return undefined; }
    const r = requestAnimationFrame(() => setMonte(true));
    return () => cancelAnimationFrame(r);
  }, []);

  const debut = (e) => {
    if (t.sortie || (e.pointerType === "mouse" && e.button !== 0)) return;
    if (e.target && e.target.closest && e.target.closest("button, a")) return;
    geste.current = { id: e.pointerId, x0: e.clientX, y0: e.clientY, dx: 0, actif: false, hist: [[e.timeStamp, e.clientX]] };
  };
  const mouvement = (e) => {
    const g = geste.current;
    if (!g || e.pointerId !== g.id) return;
    const dx = e.clientX - g.x0;
    if (!g.actif) {
      if (Math.abs(dx) < 6) return;
      if (Math.abs(e.clientY - g.y0) > Math.abs(dx)) { geste.current = null; return; }
      g.actif = true;
      g.x0 += Math.sign(dx) * 6;
      onPause(glisser, true);
      ref.current.setAttribute("data-glisse", "");
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* jsdom */ }
    }
    g.dx = e.clientX - g.x0;
    ref.current.style.setProperty("--glisser", `${g.dx.toFixed(1)}px`);
    g.hist.push([e.timeStamp, e.clientX]);
    if (g.hist.length > 5) g.hist.shift();
  };
  const fin = (e) => {
    const g = geste.current;
    if (!g || e.pointerId !== g.id) return;
    geste.current = null;
    if (!g.actif) return;
    const el = ref.current;
    el.removeAttribute("data-glisse");
    onPause(glisser, false);
    const a = g.hist[0];
    const b = g.hist[g.hist.length - 1];
    const v = e.timeStamp - b[0] > 90 ? 0 : (b[0] - a[0] > 0 ? (b[1] - a[1]) / (b[0] - a[0]) : 0);
    if (Math.abs(g.dx) > PILE.seuilGlisser || Math.abs(v) > PILE.vitesseGlisser) {
      el.style.setProperty("--sortie-x", `${Math.sign(g.dx || v) * 110}%`);
      onFermer();
    } else {
      el.style.removeProperty("--glisser");
    }
  };

  return (
    <div ref={ref} className="enio-toast" data-type={t.type}
      data-monte={monte ? "true" : "false"} data-sortie={t.sortie ? "true" : "false"}
      data-cache={visible ? "false" : "true"}
      role={t.type === "error" ? "alert" : "status"} aria-atomic="true"
      style={{ "--offset": `${offset}px` }}
      onPointerEnter={(e) => { if (e.pointerType === "mouse") onPause(survol, true); }}
      onPointerLeave={(e) => { if (e.pointerType === "mouse") onPause(survol, false); }}
      onPointerDown={debut} onPointerMove={mouvement} onPointerUp={fin} onPointerCancel={fin} onLostPointerCapture={fin}>
      <span className="enio-toast-ico" aria-hidden="true">
        <Icone name={ICONE_PAR_TYPE[t.type] || "bell"} size={18} />
      </span>
      <div className="enio-toast-msg">{t.message}</div>
      <button type="button" className="enio-toast-x" onClick={onFermer} aria-label="Fermer la notification">
        <Icone name="x" size={16} />
      </button>
    </div>
  );
}

// Helper callable depuis n'importe quel code (event handlers async, utilitaires,
// services). N'exige pas d'être dans un composant React.
// Usage : toast.error("Échec : ...")
export const toast = {
  success: (msg, opts) => _globalToastApi ? _globalToastApi.success(msg, opts) : console.log("[toast:success]", msg),
  error:   (msg, opts) => _globalToastApi ? _globalToastApi.error(msg, opts)   : console.warn("[toast:error]", msg),
  info:    (msg, opts) => _globalToastApi ? _globalToastApi.info(msg, opts)    : console.info("[toast:info]", msg),
  warn:    (msg, opts) => _globalToastApi ? _globalToastApi.warn(msg, opts)    : console.warn("[toast:warn]", msg),
  dismiss: (id) => { if (_globalToastApi) _globalToastApi.dismiss(id); },
};

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Fallback graceful si utilisé hors provider : use console
    return {
      success: m => console.log("[toast:success]", m),
      error:   m => console.warn("[toast:error]", m),
      info:    m => console.info("[toast:info]", m),
      warn:    m => console.warn("[toast:warn]", m),
      dismiss: () => {},
    };
  }
  return ctx;
}
